import { Container, Stack, styled, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import * as React from 'react';
import DotActiveImg from '../../../header-carousel/assets/buttons/dot-active.png';
import DotDefaultImg from '../../../header-carousel/assets/buttons/dot.png';
import LeftHoverImg from '../../../header-carousel/assets/buttons/left-hover.png';
import LeftImg from '../../../header-carousel/assets/buttons/left.png';
import RightHoverImg from '../../../header-carousel/assets/buttons/right-hover.png';
import RightImg from '../../../header-carousel/assets/buttons/right.png';
import { stepsContent } from './components/steps-content/steps-content';
import Steps from './components/steps/steps';
import WhatIsCurikoImage from './components/what-is-curiko-image/what-is-curiko-image';

const OuterStack = styled(Stack)`
  width: 100%;
  min-height: 600px;
  @media (max-width: 960px) {
    flex-direction: column;
    justify-content: center;
    margin-left: 0px !important;
  }
`;
const InnerStackGradient = styled(Stack)<{ customBorderColor: string }>`
  width: 50%;
  justify-content: space-between;
  background: linear-gradient(90deg, rgba(37, 22, 40, 0.7) 0%, rgba(0, 0, 0, 0.7) 99.98%);
  border-radius: 24px;
  padding: 36px 48px 36px 48px;
  border-left: 8px solid;
  border-color: ${({ customBorderColor }) => customBorderColor};
  transition: border-color 0.5s;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
    border-left: none;
    border-top: 8px solid;
    padding: 36px 24px 36px 24px;
    margin-bottom: 20px;
    border-color: ${({ customBorderColor }) => customBorderColor};
  }
`;

const InnerStack = styled(Stack)`
  width: 50%;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const Controls = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  align-self: flex-start;
  padding: 0;
`;

const ArrowButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 10px;
  position: relative;
  display: flex;

  .arrow-default {
    transition: opacity 0.3s ease-in-out;
  }

  .arrow-hover {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover .arrow-default {
    opacity: 0;
  }

  &:hover .arrow-hover {
    opacity: 1;
  }
`;

const Dots = styled('div')`
  display: flex;
  align-items: center;
`;

const Dot = styled('div')<{ active: boolean }>`
  width: ${props => (props.active ? '24px' : '12px')};
  height: ${props => (props.active ? '24px' : '12px')};
  margin: 0 5px;
  background: ${props =>
    props.active ? `url('${DotActiveImg.src}')` : `url('${DotDefaultImg.src}')`};
  background-size: cover;
  scale: ${props => (props.active ? 1.5 : 1)};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: url('${DotActiveImg.src}');
    background-size: cover;
    scale: ${props => (props.active ? 1.5 : 2.5)};
  }
`;

const FadeWrapper = styled('div')<{ isFading: boolean }>`
  transition: opacity 1s;
  opacity: ${({ isFading }) => (isFading ? 0 : 1)};
`;

export default function WhatIsCurikoStepper() {
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isFading, setIsFading] = React.useState(false);
  const fadeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const maxStep = stepsContent.length - 1;

  React.useEffect(() => {
    return () => {
      if (fadeTimer.current) {
        clearTimeout(fadeTimer.current);
      }
    };
  }, []);

  const handleNext = () => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(prev => (prev !== maxStep ? prev + 1 : 0));
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  const handleBack = () => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(prev => (prev !== 0 ? prev - 1 : maxStep));
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  const handleDotClick = (index: number) => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(index);
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  return (
    <>
      <OuterStack direction="row">
        {isMobile && (
          <InnerStack>
            <WhatIsCurikoImage activeStep={activeStep} />
          </InnerStack>
        )}
        <InnerStackGradient customBorderColor={stepsContent[activeStep].color}>
          <FadeWrapper isFading={isFading}>
            <Steps step={activeStep} />
          </FadeWrapper>
          <Controls>
            <ArrowButton onClick={handleBack}>
              <div className="arrow-default">
                <Image width={64} height={64} src={LeftImg} alt="Previous" />
              </div>
              <div className="arrow-hover">
                <Image width={64} height={64} src={LeftHoverImg} alt="Previous" />
              </div>
            </ArrowButton>
            <Dots>
              {stepsContent.map((_, index) => (
                <Dot
                  key={index}
                  active={index === activeStep}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </Dots>
            <ArrowButton onClick={handleNext}>
              <div className="arrow-default">
                <Image width={64} height={64} src={RightImg} alt="Next" />
              </div>
              <div className="arrow-hover">
                <Image width={64} height={64} src={RightHoverImg} alt="Next" />
              </div>
            </ArrowButton>
          </Controls>
        </InnerStackGradient>
        {!isMobile && (
          <InnerStack>
            <FadeWrapper isFading={isFading}>
              <WhatIsCurikoImage activeStep={activeStep} />
            </FadeWrapper>
          </InnerStack>
        )}
      </OuterStack>
    </>
  );
}
